import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useRef, useState, useMemo } from 'react';
import { Trail } from '@react-three/drei';
import { useGameStore } from '../stores/gameStore.js';

export default function PlayerTrail({ playerRef, colorStart = "#00ffff", colorEnd = "#00b3ff" }) {
    const { scene } = useThree();
    const targetRefLeft = useRef(null);
    const targetRefRight = useRef(null);

    // Audio
    const analyser = useGameStore((state) => state.audio.analyser);
    const phase = useGameStore((state) => state.phase);
    const dataArray = useMemo(() => new Uint8Array(128), []);

    // Simulation
    const lastPosLeft = useRef(new THREE.Vector3());
    const lastPosRight = useRef(new THREE.Vector3());

    // INSTANCED MESH POOL state (PERFORMANCE FIX 🚀)
    const MAX_PARTICLES = 250;
    const instancedMeshRef = useRef();
    const particlesData = useRef(Array.from({ length: MAX_PARTICLES }, () => ({
        active: false,
        age: 0,
        position: new THREE.Vector3(),
        velocity: new THREE.Vector3(),
        rotation: new THREE.Euler(),
        rotationSpeed: new THREE.Euler(),
        initialScale: 1
    })));
    const particleIndex = useRef(0);
    const dummy = useMemo(() => new THREE.Object3D(), []);

    // Width State
    const [trailWidth, setTrailWidth] = useState(2);
    const lastWidthRef = useRef(2);

    // Config
    const trailConfig = {
        sizeCheck: 0.15,
        lifetime: 30, // Frames
        // Reseting offsets to match the Thruster cones again
        offsetLeft: new THREE.Vector3(-1, -0.25, 0.43),
        offsetRight: new THREE.Vector3(-1, -0.25, -0.43),
    };

    // Particles: Small additive octahedrons (glints)
    const particleGeo = new THREE.OctahedronGeometry(0.1, 0);
    // Reuse material to avoid reallocation
    const particleMat = useMemo(() => new THREE.MeshBasicMaterial({
        color: new THREE.Color("#00ffff"),
        transparent: true,
        opacity: 0.8,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
    }), []);

    const vLeftCache = useRef(new THREE.Vector3());
    const vRightCache = useRef(new THREE.Vector3());

    useFrame((state) => {
        const time = state.clock.elapsedTime;
        if (!playerRef.current) return;

        if (!targetRefLeft.current) {
            targetRefLeft.current = scene.getObjectByName('leftEngine');
            targetRefRight.current = scene.getObjectByName('rightEngine');
            if (!targetRefLeft.current) return;
        }

        // 1. Audio Analysis
        let energy = 0;
        if (analyser) {
            analyser.getByteFrequencyData(dataArray);
            let sum = 0;
            // Focus on bass/kick
            for (let i = 0; i < 10; i++) sum += dataArray[i];
            energy = sum / 10 / 255;
        }

        // 2. Dynamic Width - High Contrast Pulse
        let targetWidth;

        if (phase === 'launching') {
            // MAX WIDTH FOR HYPERSPACE EFFECT
            targetWidth = 7.5;
        } else if (phase === 'ended') {
            targetWidth = 0;
        } else {
            const pulse = Math.pow(energy, 3);
            targetWidth = 0.6 + pulse * 15.0; // Increased min size to prevent "cut" effect
        }

        // Smooth width transition to avoid jagged geometry
        lastWidthRef.current = THREE.MathUtils.lerp(lastWidthRef.current, targetWidth, 0.1);
        setTrailWidth(lastWidthRef.current);

        // 3. Update Target Positions
        const leftEngine = scene.getObjectByName('leftEngine');
        const rightEngine = scene.getObjectByName('rightEngine');

        if (leftEngine && targetRefLeft.current) {
            leftEngine.getWorldPosition(targetRefLeft.current.position);
            leftEngine.getWorldQuaternion(targetRefLeft.current.quaternion);
        }
        if (rightEngine && targetRefRight.current) {
            rightEngine.getWorldPosition(targetRefRight.current.position);
            rightEngine.getWorldQuaternion(targetRefRight.current.quaternion);
        }

        // 4. Particles Spawning
        const handleParticles = (emissionPos, lastPosRef) => {
            const spawnDist = trailConfig.sizeCheck / (1 + energy * 3.0);

            if (lastPosRef.current.distanceTo(emissionPos) > spawnDist) {
                const count = energy > 0.5 ? 2 : 1;

                for (let k = 0; k < count; k++) {
                    // Ring Buffer extraction (NO NEW MESHES)
                    const idx = particleIndex.current;
                    const p = particlesData.current[idx];

                    p.active = true;
                    p.age = 0;

                    const spread = 0.05 + (energy * 0.2);
                    p.position.copy(emissionPos).add(new THREE.Vector3(
                        (Math.random() - 0.5) * spread,
                        (Math.random() - 0.5) * spread,
                        (Math.random() - 0.5) * spread
                    ));

                    p.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, 0);

                    const scaleBase = 0.5 + Math.random() * 0.5;
                    p.initialScale = scaleBase * (1 + energy);

                    p.velocity.set(
                        (Math.random() - 0.5) * 0.05,
                        (Math.random() - 0.5) * 0.05,
                        (Math.random() - 0.5) * 0.05
                    );

                    p.rotationSpeed.set(Math.random() * 0.2, Math.random() * 0.2, 0);

                    // Move index forward cyclically
                    particleIndex.current = (idx + 1) % MAX_PARTICLES;
                }
                lastPosRef.current.copy(emissionPos);
            }
        };

        if (phase !== 'ended' && targetRefLeft.current && targetRefRight.current) {
            handleParticles(targetRefLeft.current.position, lastPosLeft);
            handleParticles(targetRefRight.current.position, lastPosRight);
        }

        // 5. Update Particles per InstancedMesh
        if (instancedMeshRef.current) {
            for (let i = 0; i < MAX_PARTICLES; i++) {
                const p = particlesData.current[i];
                if (p.active) {
                    p.age++;
                    const lifeRatio = p.age / trailConfig.lifetime;

                    if (lifeRatio >= 1) {
                        p.active = false;
                        dummy.scale.set(0, 0, 0); // Hide visually scaling to 0 instead of fading opacity
                    } else {
                        const currentScale = p.initialScale * ((1 - lifeRatio) * (1 - lifeRatio)); // Ease out quad

                        p.position.add(p.velocity);
                        p.rotation.x += p.rotationSpeed.x;
                        p.rotation.y += p.rotationSpeed.y;

                        dummy.position.copy(p.position);
                        dummy.rotation.copy(p.rotation);
                        dummy.scale.setScalar(currentScale);
                    }
                    dummy.updateMatrix();
                    instancedMeshRef.current.setMatrixAt(i, dummy.matrix);
                } else {
                    // Make sure it remains invisible if not active
                    dummy.scale.set(0, 0, 0);
                    dummy.updateMatrix();
                    instancedMeshRef.current.setMatrixAt(i, dummy.matrix);
                }
            }
            instancedMeshRef.current.instanceMatrix.needsUpdate = true;
        }
    });

    return (
        <>
            <instancedMesh ref={instancedMeshRef} args={[particleGeo, particleMat, MAX_PARTICLES]} frustumCulled={false} />

            <group ref={targetRefLeft}><mesh visible={false} /></group>
            <group ref={targetRefRight}><mesh visible={false} /></group>

            {/* Outer Blue Trail */}
            <Trail
                width={trailWidth}
                length={12} // Longer trail for smoother look at speed
                attenuation={(t) => t} // Linear attenuation (smoother fade than t*t)
                target={targetRefLeft}
                color={colorEnd}
                material-transparent={true}
                material-opacity={0.8}
                material-depthWrite={false}
                material-blending={THREE.AdditiveBlending}
                interval={1} // Force update every frame
                renderOrder={1}
            />

            <Trail
                width={trailWidth}
                length={12}
                attenuation={(t) => t}
                target={targetRefRight}
                color={colorEnd}
                material-transparent={true}
                material-opacity={0.8}
                material-depthWrite={false}
                material-blending={THREE.AdditiveBlending}
                interval={1}
                renderOrder={1}
            />

            {/* NEON CORE EFFECT (White Center) */}
            <Trail
                width={Math.max(0.4, trailWidth * 0.3)}
                length={12}
                attenuation={(t) => t}
                target={targetRefLeft}
                color={'#ffffff'}
                material-transparent={true}
                material-opacity={0.8}
                material-depthWrite={false}
                material-blending={THREE.AdditiveBlending}
                interval={1}
                renderOrder={2}
            />
            <Trail
                width={Math.max(0.4, trailWidth * 0.3)}
                length={12}
                attenuation={(t) => t}
                target={targetRefRight}
                color={'#ffffff'}
                material-transparent={true}
                material-opacity={0.8}
                material-depthWrite={false}
                material-blending={THREE.AdditiveBlending}
                interval={1}
                renderOrder={2}
            />
        </>
    );
}
